import { useMemo } from 'react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { useTooltip, Chip } from '@frontend/design-system';

interface GenericChipListItem {
  name: string;
  [key: string]: any; // Allow additional properties
}

type GenericChipsListProps<T extends GenericChipListItem> = {
  items?: T[];
  maxVisible?: number;
  renderChip: (item: T) => React.ReactNode;
  className?: string;
};

export const GenericChipsList = <T extends GenericChipListItem>({
  items = [],
  maxVisible = 2,
  renderChip,
  className,
}: GenericChipsListProps<T>) => {
  const { t } = useTranslation('schedule');
  const { Tooltip, tooltipProps, triggerProps } = useTooltip();
  const { visibleItems, hiddenItems } = useMemo(() => {
    const visibleItems: T[] = [];
    const hiddenItems: T[] = [];

    items
      .filter((item, index, self) => item.name && self.findIndex((t) => t.name === item.name) === index) // Remove duplicates & empty names
      .forEach((item, index, arr) => {
        if (index + 1 < maxVisible || arr.length === maxVisible) {
          visibleItems.push(item);
        } else {
          hiddenItems.push(item);
        }
      });

    return { visibleItems, hiddenItems };
  }, [items, maxVisible]);

  if (!visibleItems.length) {
    return null;
  }

  return (
    <section css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(0.5) }} className={className}>
      {visibleItems.map((item) => renderChip(item))}
      {hiddenItems.length > 0 && (
        <>
          <Chip variant='neutral' {...triggerProps}>
            {t('+{{value}} more', {
              value: hiddenItems.length,
            })}
          </Chip>
          <Tooltip {...tooltipProps}>{hiddenItems.map(({ name }) => name).join(', ')}</Tooltip>
        </>
      )}
    </section>
  );
};
